import 'react-datepicker/dist/react-datepicker.css';
import './DatePicker.css';

import ReactDatePicker, { registerLocale, setDefaultLocale } from 'react-datepicker';
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../../helpers/i18n';

import { enUS, de } from 'date-fns/locale';

type PickerProps = {
    type?: 'date' | 'time' | 'datetime';
    disabled?: boolean;
    nowButton?: boolean;
    callback?: (date: Date) => void;
    selected?: Date;
    timeIntervals?: number;
};

registerLocale('enUS', enUS);
registerLocale('de', de);

const getDatePickerLocale = (): string => {
    const currentLang = i18n.language || 'en-US';
    if (currentLang.startsWith('de')) {
        return 'de';
    }
    return 'enUS';
};

setDefaultLocale(getDatePickerLocale());

const DatePicker = ({
    type,
    disabled = true,
    nowButton,
    callback,
    selected,
    timeIntervals,
}: PickerProps): JSX.Element => {
    const { t } = useTranslation();
    const [startDate, setStartDate] = useState<Date>();
    const datepickerRef = useRef<HTMLDivElement>(null);
    const currentLocale = getDatePickerLocale();

    const isDateTime = type === 'datetime';
    const isDateOnly = type === 'date' || type === undefined;
    const isTimeOnly = type === 'time';

    useEffect(() => {
        if (isDateOnly) {
            const updateRecurringEndHeight = (isOpen: boolean) => {
                const recurringEnd = datepickerRef.current?.closest('.frontpage__recurring-end');
                if (recurringEnd) {
                    if (isOpen) {
                        recurringEnd.classList.add('datepicker-open');
                    } else {
                        recurringEnd.classList.remove('datepicker-open');
                    }
                }
            };

            // Watch for calendar popper appearing/disappearing
            const checkCalendarState = () => {
                const popper = document.querySelector('.react-datepicker-popper');
                const isOpen = popper !== null && popper.parentElement !== null;
                updateRecurringEndHeight(isOpen);
            };

            // Check periodically
            const intervalId = setInterval(checkCalendarState, 150);

            // Also check on document clicks
            const handleClick = () => {
                setTimeout(checkCalendarState, 50);
            };

            document.addEventListener('click', handleClick);

            // Observe DOM changes
            const observer = new MutationObserver(checkCalendarState);
            observer.observe(document.body, {
                childList: true,
                subtree: true,
            });

            return () => {
                clearInterval(intervalId);
                document.removeEventListener('click', handleClick);
                observer.disconnect();
                updateRecurringEndHeight(false);
            };
        }
    }, [isDateOnly]);

    return (
        <div className="datepicker" ref={datepickerRef}>
            <ReactDatePicker
                disabled={disabled}
                placeholderText={isTimeOnly ? '00:00' : isDateTime ? t('dd.mm.yyyy 00:00') : t('dd.mm.yyyy')}
                selected={selected || startDate}
                onChange={(date: Date) => {
                    setStartDate(date);
                    callback && callback(date);
                }}
                todayButton={nowButton ? t('Today') : undefined}
                locale={currentLocale}
                showTimeSelect={isTimeOnly || isDateTime}
                showTimeSelectOnly={isTimeOnly}
                timeIntervals={isDateTime ? timeIntervals || 15 : undefined}
                dateFormat={isTimeOnly ? 'HH:mm' : isDateTime ? 'dd.MM.yyyy HH:mm' : 'dd.MM.yyyy'}
                timeCaption={t('Time')}
                popperPlacement="bottom-start"
            />
            <i className={isTimeOnly ? 'time-icon' : 'calendar-icon'} aria-label="datepicker" />
        </div>
    );
};

export default DatePicker;
